import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Eye, Type, Zap, Mic, Volume2,
  Palette, Settings2, User, CreditCard, LogOut, Sparkles,
  BookHeart, Languages, Crown, Lock, RefreshCw
} from 'lucide-react';
import ClearCacheButton from '@/components/ClearCacheButton';
import NarratorSettings from '@/components/NarratorSettings';

const SettingsPage = () => {
  const { 
    theme, setTheme, 
    colorMode, toggleColorMode,
    accessibility, updateAccessibility,
    isKidsMode, isDarkMode
  } = useTheme();
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  
  // Curriculum settings state
  const [curriculumSettings, setCurriculumSettings] = useState({
    bible_curriculum_enabled: false,
    languages_enabled: [],
    available_languages: [],
    bible_available: false,
    subscription_plan: 'free'
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchCurriculumSettings();
  }, []);

  const fetchCurriculumSettings = async () => {
    try {
      const response = await api.get('/user/curriculum-settings');
      setCurriculumSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch curriculum settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const updateCurriculumSetting = async (key, value) => {
    setSavingSettings(true);
    try {
      let newSettings = { ...curriculumSettings };
      
      if (key === 'bible_curriculum_enabled') {
        newSettings.bible_curriculum_enabled = value;
      } else if (key === 'language') {
        const langId = value.id;
        if (newSettings.languages_enabled.includes(langId)) {
          newSettings.languages_enabled = newSettings.languages_enabled.filter(l => l !== langId);
        } else {
          newSettings.languages_enabled = [...newSettings.languages_enabled, langId];
        }
      }
      
      const response = await api.put('/user/curriculum-settings', {
        bible_curriculum_enabled: newSettings.bible_curriculum_enabled,
        languages_enabled: newSettings.languages_enabled
      });
      
      setCurriculumSettings(prev => ({
        ...prev,
        bible_curriculum_enabled: response.data.bible_curriculum_enabled,
        languages_enabled: response.data.languages_enabled
      }));
      
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Settings2 className="w-6 h-6 text-slate-600" />
              <h1 className="font-bold text-xl text-slate-800">Settings</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Account */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-sky-500" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2 border-t pt-4">
              <div>
                <p className="font-medium">Subscription</p>
                <p className="text-sm text-slate-500 capitalize">{user?.subscription_plan || 'Free'} Plan</p>
              </div>
              <Link to="/pricing">
                <Button variant="outline" size="sm" data-testid="manage-subscription-btn">
                  <CreditCard className="w-4 h-4 mr-2" /> Manage
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-between py-2 border-t pt-4">
              <div className="pr-3">
                <p className="font-medium flex items-center gap-1">
                  <RefreshCw className="w-4 h-4 text-slate-400" /> App cache
                </p>
                <p className="text-sm text-slate-500">
                  Seeing outdated lessons or stuck content? Clear the cache to force fresh downloads. You'll stay signed in.
                </p>
              </div>
              <ClearCacheButton testId="settings-clear-cache-btn" />
            </div>
          </CardContent>
        </Card>

        {/* Grown-up Controls · Narrator Voice */}
        <NarratorSettings />

        {/* Curriculum Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookHeart className="w-5 h-5 text-rose-500" />
              Curriculum Settings
            </CardTitle>
            <CardDescription>Choose which subjects to include for your children</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingSettings ? (
              <div className="text-center py-4 text-slate-500">Loading settings...</div>
            ) : (
              <>
                {/* Bible Curriculum */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                      <BookHeart className="w-5 h-5 text-rose-500" />
                    </div>
                    <div>
                      <p className="font-medium">Bible Curriculum</p>
                      <p className="text-sm text-slate-500">
                        Christian stories, values, and memory verses
                      </p>
                    </div>
                  </div>
                  {curriculumSettings.bible_available ? (
                    <Switch
                      checked={curriculumSettings.bible_curriculum_enabled}
                      onCheckedChange={(checked) => updateCurriculumSetting('bible_curriculum_enabled', checked)}
                      disabled={savingSettings}
                      data-testid="bible-toggle"
                    />
                  ) : (
                    <Link to="/pricing">
                      <Button variant="outline" size="sm" className="text-amber-600 border-amber-300">
                        <Crown className="w-4 h-4 mr-1" /> Upgrade
                      </Button>
                    </Link>
                  )}
                </div>

                {/* Languages - Temporarily hidden per product decision */}
                {false && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Languages className="w-5 h-5 text-blue-500" />
                    <p className="font-medium">Language Learning</p>
                  </div>
                  
                  <div className="space-y-3">
                    {curriculumSettings.available_languages?.map((lang) => (
                      <div key={lang.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {lang.id === 'spanish' ? '🇪🇸' : lang.id === 'french' ? '🇫🇷' : '🇬🇭'}
                          </span>
                          <div>
                            <p className="font-medium">{lang.name}</p>
                            <p className="text-xs text-slate-500">
                              {lang.id === 'akan' ? 'Includes Twi & Fante dialects' : 'Vocabulary, numbers, phrases'}
                            </p>
                          </div>
                        </div>
                        {lang.available ? (
                          <Switch
                            checked={curriculumSettings.languages_enabled?.includes(lang.id)}
                            onCheckedChange={() => updateCurriculumSetting('language', lang)}
                            disabled={savingSettings}
                            data-testid={`language-toggle-${lang.id}`}
                          />
                        ) : (
                          <div className="flex items-center gap-1 text-slate-400">
                            <Lock className="w-4 h-4" />
                            <span className="text-xs">{lang.id === 'spanish' ? 'Basic+' : 'Premium'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {curriculumSettings.subscription_plan === 'free' && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      <p className="font-medium text-amber-800">Unlock More Content</p>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                      Upgrade to access Bible curriculum, language learning, and more!
                    </p>
                    <Link to="/pricing">
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                        View Plans
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-500" />
              Appearance
            </CardTitle>
            <CardDescription>Customize how the app looks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Theme Mode */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  {isKidsMode ? <Sparkles className="w-5 h-5 text-sky-500" /> : <User className="w-5 h-5 text-sky-500" />}
                </div>
                <div>
                  <p className="font-medium">Interface Mode</p>
                  <p className="text-sm text-slate-500">
                    {isKidsMode ? 'Kids Mode - Fun and colorful' : 'Parent Mode - Clean and organized'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="theme-mode" className="text-sm">Kids</Label>
                <Switch
                  id="theme-mode"
                  checked={!isKidsMode}
                  onCheckedChange={() => setTheme(isKidsMode ? 'parents' : 'kids')}
                  data-testid="theme-mode-toggle"
                />
                <Label htmlFor="theme-mode" className="text-sm">Parent</Label>
              </div>
            </div>

            {/* Color Mode */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  {isDarkMode ? <Moon className="w-5 h-5 text-yellow-600" /> : <Sun className="w-5 h-5 text-yellow-600" />}
                </div>
                <div>
                  <p className="font-medium">Color Mode</p>
                  <p className="text-sm text-slate-500">
                    {isDarkMode ? 'Dark mode enabled' : 'Light mode enabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={isDarkMode}
                onCheckedChange={toggleColorMode}
                data-testid="color-mode-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Accessibility */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-500" />
              Accessibility
            </CardTitle>
            <CardDescription>Make the app easier to use</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* High Contrast */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium">High Contrast</p>
                  <p className="text-sm text-slate-500">Increase visual contrast for better visibility</p>
                </div>
              </div>
              <Switch
                checked={accessibility.highContrast}
                onCheckedChange={(checked) => updateAccessibility('highContrast', checked)}
                data-testid="high-contrast-toggle"
              />
            </div>

            {/* Large Text */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Type className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Large Text</p>
                  <p className="text-sm text-slate-500">Increase text size throughout the app</p>
                </div>
              </div>
              <Switch
                checked={accessibility.largeText}
                onCheckedChange={(checked) => updateAccessibility('largeText', checked)}
                data-testid="large-text-toggle"
              />
            </div>

            {/* Reduced Motion */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium">Reduced Motion</p>
                  <p className="text-sm text-slate-500">Minimize animations and movement</p>
                </div>
              </div>
              <Switch
                checked={accessibility.reducedMotion}
                onCheckedChange={(checked) => updateAccessibility('reducedMotion', checked)}
                data-testid="reduced-motion-toggle"
              />
            </div>

            {/* Voice Input */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Mic className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium">Voice Input</p>
                  <p className="text-sm text-slate-500">Allow speaking answers in activities</p>
                </div>
              </div>
              <Switch
                checked={accessibility.voiceEnabled}
                onCheckedChange={(checked) => updateAccessibility('voiceEnabled', checked)}
                data-testid="voice-enabled-toggle"
              />
            </div>

            {/* Text-to-Speech */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Text-to-Speech</p>
                  <p className="text-sm text-slate-500">Read instructions and content aloud</p>
                </div>
              </div>
              <Switch
                checked={accessibility.ttsEnabled}
                onCheckedChange={(checked) => updateAccessibility('ttsEnabled', checked)}
                data-testid="tts-enabled-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Account Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" /> Sign Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SettingsPage;
